import type { CatalogResponse } from '@nadoutong/contracts';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getGatewayBaseUrl } from '../../api/client.ts';
import { useCatalog } from '../../api/useCatalog.ts';
import { localizeApiError } from '../../api/errorMessages.ts';
import { useToast } from '../../components/Toast.tsx';
import type { I18nKey } from '../../i18n/index.ts';
import { useT } from '../../i18n/index.ts';
import {
  Button,
  buttonClass,
  cx,
  Icon,
  PageHeader,
  Panel,
  StatusLamp,
  Table,
  Tabs,
  TabsList,
  TabsTrigger,
  TBody,
  Td,
  THead,
  Th,
  Tr,
} from '../../ui/index.ts';
import type { DocsPlatform } from '../docsSnippets.ts';
import {
  anthropicCurlSnippet,
  CC_SWITCH_INSTALL_SNIPPET,
  CLAUDE_CODE_INSTALL_SNIPPET,
  claudeLongTermSnippet,
  claudeTempEnvSnippet,
  codexConfigSnippet,
  codexEnvSnippet,
  disableClaudeBetasSnippet,
  ENV_CHECK_SNIPPET,
  EXAMPLE_ANTHROPIC_MODEL,
  EXAMPLE_IMAGE_MODEL,
  EXAMPLE_OPENAI_MODEL,
  EXAMPLE_SEEDANCE_MODEL,
  EXAMPLE_VIDEO_MODEL,
  FALLBACK_ANTHROPIC_MODELS,
  FALLBACK_OPENAI_MODELS,
  imagesCurlBothSnippet,
  imagesSdkSnippet,
  lobechatEnvSnippet,
  modelsCurlSnippet,
  openaiCurlSnippet,
  openclawProviderSnippet,
  SEEDANCE_REFERENCE_IMAGE_SNIPPET,
  SEEDANCE_REFERENCE_VIDEO_SNIPPET,
  seedanceCurlBothSnippet,
  seedancePythonSnippet,
  videosCurlBothSnippet,
  videosSdkSnippet,
} from '../docsSnippets.ts';
import { EndpointCards } from './EndpointCards.tsx';

/**
 * Bundled `/docs` copy — the conservative fallback while the served
 * document is loading, fails, or ships a `presentation_version` this
 * build does not know. Deletion is #152; do not treat this file as the
 * live page once `Docs.tsx` is rendering `GET /v1/docs/access`.
 *
 * The three endpoint fact cards live in `EndpointCards.tsx` (environment-
 * derived local chrome; they survive #152). All prose is served from the
 * i18n dictionaries (`docs.*`); code snippets stay code and live in
 * `docsSnippets.ts` together with the illustrative model literals.
 * Model names in prose and snippets come from the live catalog SKU
 * market when available and always use the canonical `namespace/model`
 * form — bare names only resolve through per-key model mappings.
 *
 * Base URLs are never hardcoded here: `getGatewayBaseUrl()` resolves
 * them per host at runtime (see `src/api/client.ts`), so the page shows
 * the endpoint the reader's own environment actually talks to.
 *
 * Layout: sticky section TOC on xl; below xl the TOC renders as a
 * horizontal anchor chip row at the top instead of disappearing.
 *
 * FILE-LENGTH EXEMPTION — read before "cleaning this up". This file is a
 * file-for-file port of `console-web/src/pages/access/Docs.tsx` and is kept
 * structurally isomorphic with it on purpose, so the two can still be
 * diffed against each other while both brands ship the same integration
 * story. Splitting it here alone would fork it from its source and make
 * every later reconciliation a manual read. The house ~800-line limit is
 * therefore waived for this file specifically; if console-web splits it
 * first, this side follows with the same split.
 */

type SectionId =
  | 'prepare'
  | 'models'
  | 'create-key'
  | 'claude-code'
  | 'codex'
  | 'cc-switch'
  | 'openclaw'
  | 'clients'
  | 'claude-desktop'
  | 'vscode'
  | 'images'
  | 'videos'
  | 'seedance'
  | 'verify';

const SECTIONS: readonly { readonly id: SectionId; readonly labelKey: I18nKey }[] = [
  { id: 'prepare', labelKey: 'docs.section.prepare.label' },
  { id: 'models', labelKey: 'docs.section.models.label' },
  { id: 'create-key', labelKey: 'docs.section.createKey.label' },
  { id: 'claude-code', labelKey: 'docs.section.claudeCode.label' },
  { id: 'codex', labelKey: 'docs.section.codex.label' },
  { id: 'cc-switch', labelKey: 'docs.section.ccSwitch.label' },
  { id: 'openclaw', labelKey: 'docs.section.openclaw.label' },
  { id: 'clients', labelKey: 'docs.section.clients.label' },
  { id: 'claude-desktop', labelKey: 'docs.section.claudeDesktop.label' },
  { id: 'vscode', labelKey: 'docs.section.vscode.label' },
  { id: 'images', labelKey: 'docs.section.images.label' },
  { id: 'videos', labelKey: 'docs.section.videos.label' },
  { id: 'seedance', labelKey: 'docs.section.seedance.label' },
  { id: 'verify', labelKey: 'docs.section.verify.label' },
];

/**
 * Which section the reader is currently looking at, for the TOC's active
 * state. Both TOCs list every section at once, so without this there is
 * nothing tying an entry to what is actually on screen.
 *
 * `IntersectionObserver` against the viewport (`root: null`), deliberately: it
 * stays correct whether the page scrolls at the window or inside a shell
 * container, which a scroll listener would have to guess.
 *
 * `rootMargin` narrows the observation to a strip near the top, so "current"
 * means "the heading you just scrolled under". With a full-viewport band
 * several sections qualify at once and the highlight stops meaning anything.
 *
 * Active is the LAST intersecting section in document order, not the first.
 * Both are in the strip during the handover between two sections, and the one
 * the reader has arrived at is the later one — its heading is what just
 * crossed the top line. Taking the first instead lags a whole section behind:
 * scrolling to `#codex` would light `claude-code`.
 *
 * When the strip holds nothing — at the very bottom, where the last section's
 * top is already above it — the previous value stands rather than clearing, so
 * the highlight never blinks off mid-read.
 *
 * Feature-detected: happy-dom (the test DOM) has no IntersectionObserver, and
 * a missing highlight must not take the page down with it.
 */
function useActiveSection(): SectionId | null {
  const [active, setActive] = useState<SectionId | null>(null);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;

    const intersecting = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) intersecting.add(entry.target.id);
          else intersecting.delete(entry.target.id);
        }
        let current: SectionId | null = null;
        for (const section of SECTIONS) {
          if (intersecting.has(section.id)) current = section.id;
        }
        if (current !== null) setActive(current);
      },
      { rootMargin: '-72px 0px -68% 0px', threshold: 0 },
    );

    for (const section of SECTIONS) {
      const el = document.getElementById(section.id);
      if (el !== null) observer.observe(el);
    }
    return () => {
      observer.disconnect();
    };
  }, []);

  return active;
}

/**
 * What the Images routes do with each parameter they accept.
 *
 * Written from measurement, not from the OpenAI reference: several parameters
 * are accepted and then not honoured on this route, and a table that repeated
 * the reference would be documenting a different service.
 *
 * Split per route rather than one merged table with a "which route" column:
 * the two share most parameters but differ on the ones that decide whether a
 * request is even valid (`image` is required on one and rejected on the
 * other), and a merged table makes the reader resolve that per row.
 *
 * `required` and `default` are shown for every row. A parameter with no
 * default says so explicitly — an empty cell reads as "not documented" rather
 * than "there isn't one".
 *
 * Only defaults THIS service decides are stated. `quality`, `background` and
 * `output_format` are settled further down the chain when omitted, so their
 * default is listed as none: printing a number nobody here can hold would be
 * publishing a promise that has changed before.
 */
interface ImageParamRow {
  readonly param: string;
  /** Omitted renders as the dictionary's "none" label, never a blank cell. */
  readonly defaultValue?: string;
  readonly required: boolean;
  readonly behaviourKey: I18nKey;
}

/**
 * Shared by both routes; `image` / `mask` are spliced in for `edits`.
 *
 * `n`, `response_format` and `background` accept exactly one value each and
 * `quality` is accepted then ignored — so as knobs they are worthless, and
 * dropping them from the table was tried. They stay because the rejection a
 * tenant gets for setting one is the shared sanitized `REQUEST_NOT_SUPPORTED`
 * body with a null `param`: nothing in the response says which field was the
 * problem. Someone copying OpenAI sample code, which routinely sets `n` and
 * `response_format`, would face a 400 with no way to find the cause. The row
 * is the only place that answer exists.
 */
const IMAGE_COMMON_PARAMS: readonly ImageParamRow[] = [
  // Absent and `auto` are the same request to this route: neither constrains
  // the aspect ratio. That is our own handling, so it is a default we own.
  { param: 'size', required: false, defaultValue: 'auto', behaviourKey: 'docs.images.param.size' },
  { param: 'quality', required: false, behaviourKey: 'docs.images.param.quality' },
  { param: 'n', required: false, defaultValue: '1', behaviourKey: 'docs.images.param.n' },
  {
    param: 'response_format',
    required: false,
    defaultValue: 'b64_json',
    behaviourKey: 'docs.images.param.response_format',
  },
  { param: 'background', required: false, behaviourKey: 'docs.images.param.background' },
  {
    param: 'output_format',
    required: false,
    behaviourKey: 'docs.images.param.output_format',
  },
  {
    param: 'output_compression',
    required: false,
    behaviourKey: 'docs.images.param.output_compression',
  },
];

const IMAGE_GENERATE_PARAMS: readonly ImageParamRow[] = [
  { param: 'model', required: true, behaviourKey: 'docs.images.param.model' },
  { param: 'prompt', required: true, behaviourKey: 'docs.images.param.prompt' },
  ...IMAGE_COMMON_PARAMS,
];

const IMAGE_EDIT_PARAMS: readonly ImageParamRow[] = [
  { param: 'model', required: true, behaviourKey: 'docs.images.param.model' },
  { param: 'prompt', required: true, behaviourKey: 'docs.images.param.prompt' },
  { param: 'image', required: true, behaviourKey: 'docs.images.param.image' },
  { param: 'mask', required: false, behaviourKey: 'docs.images.param.mask' },
  ...IMAGE_COMMON_PARAMS,
];

/**
 * What `POST /v1/videos/generations` does with each parameter it accepts.
 *
 * Written from the gateway as wired, not from the xAI reference: several
 * official fields are rejected (`image` / `image_url` / `images`,
 * `reference_audios` / `voices`) or silently dropped (`aspect_ratio` /
 * `generate_audio`). `reference_images` is wired and billed. A table
 * that repeated the reference would be documenting a different service.
 */
interface VideoParamRow {
  readonly param: string;
  /** Omitted renders as the dictionary's "none" label, never a blank cell. */
  readonly defaultValue?: string;
  readonly required: boolean;
  readonly behaviourKey: I18nKey;
}

const VIDEO_GENERATE_PARAMS: readonly VideoParamRow[] = [
  { param: 'model', required: true, behaviourKey: 'docs.videos.param.model' },
  { param: 'prompt', required: true, behaviourKey: 'docs.videos.param.prompt' },
  { param: 'resolution', required: true, behaviourKey: 'docs.videos.param.resolution' },
  {
    param: 'duration',
    required: false,
    defaultValue: '8',
    behaviourKey: 'docs.videos.param.duration',
  },
  {
    param: 'image / image_url / images',
    required: false,
    behaviourKey: 'docs.videos.param.image',
  },
  {
    param: 'reference_images',
    required: false,
    behaviourKey: 'docs.videos.param.reference_images',
  },
  {
    param: 'reference_audios / voices',
    required: false,
    behaviourKey: 'docs.videos.param.reference',
  },
  {
    param: 'aspect_ratio / generate_audio',
    required: false,
    behaviourKey: 'docs.videos.param.unforwarded',
  },
  {
    param: 'Idempotency-Key',
    required: false,
    behaviourKey: 'docs.videos.param.idempotency',
  },
];

/**
 * What `POST /v1/tasks/submit` does with each structured parameter.
 *
 * Written from live measurement of the ark task line, not from vendor
 * docs. The vocabulary is closed on these three names; any other
 * structured field is refused rather than ignored. Bounds differ by
 * model and are rejected before spend.
 */
interface SeedanceParamRow {
  readonly param: string;
  /** Omitted renders as the dictionary's "none" label, never a blank cell. */
  readonly defaultValue?: string;
  readonly required: boolean;
  readonly behaviourKey: I18nKey;
}

const SEEDANCE_SUBMIT_PARAMS: readonly SeedanceParamRow[] = [
  { param: 'duration', required: false, behaviourKey: 'docs.seedance.param.duration' },
  { param: 'resolution', required: false, behaviourKey: 'docs.seedance.param.resolution' },
  { param: 'ratio', required: false, behaviourKey: 'docs.seedance.param.ratio' },
];

interface ToolGuideRow {
  readonly name: string;
  readonly supplier: 'anthropic' | 'openai' | 'mixed';
  readonly base: 'anthropic' | 'openai' | 'both';
  readonly detailKey: I18nKey;
}

const TOOL_GUIDES: readonly ToolGuideRow[] = [
  {
    name: 'Claude Code',
    supplier: 'anthropic',
    base: 'anthropic',
    detailKey: 'docs.clients.tool.claude_code',
  },
  { name: 'Codex', supplier: 'openai', base: 'openai', detailKey: 'docs.clients.tool.codex' },
  { name: 'CC Switch', supplier: 'mixed', base: 'both', detailKey: 'docs.clients.tool.cc_switch' },
  { name: 'OpenClaw', supplier: 'openai', base: 'openai', detailKey: 'docs.clients.tool.openclaw' },
  {
    name: 'Cherry Studio',
    supplier: 'openai',
    base: 'openai',
    detailKey: 'docs.clients.tool.cherry_studio',
  },
  { name: 'ChatBox', supplier: 'openai', base: 'openai', detailKey: 'docs.clients.tool.chatbox' },
  { name: 'LobeChat', supplier: 'openai', base: 'openai', detailKey: 'docs.clients.tool.lobechat' },
  {
    name: 'Claude Desktop',
    supplier: 'anthropic',
    base: 'anthropic',
    detailKey: 'docs.clients.tool.claude_desktop',
  },
];

interface ClientGuide {
  readonly name: string;
  readonly keyName: string;
  readonly entryKey: I18nKey;
  readonly testKey: I18nKey;
  readonly noteKey: I18nKey;
}

const CLIENT_GUIDES: readonly ClientGuide[] = [
  {
    name: 'Cherry Studio',
    keyName: 'cherry-studio',
    entryKey: 'docs.clients.cherry.entry',
    testKey: 'docs.clients.cherry.test',
    noteKey: 'docs.clients.cherry.note',
  },
  {
    name: 'ChatBox',
    keyName: 'chatbox',
    entryKey: 'docs.clients.chatbox.entry',
    testKey: 'docs.clients.chatbox.test',
    noteKey: 'docs.clients.chatbox.note',
  },
  {
    name: 'LobeChat',
    keyName: 'lobechat',
    entryKey: 'docs.clients.lobechat.entry',
    testKey: 'docs.clients.lobechat.test',
    noteKey: 'docs.clients.lobechat.note',
  },
];

const VERIFY_ERRORS: readonly {
  readonly symptomKey: I18nKey;
  readonly reasonKey: I18nKey;
  readonly actionKey: I18nKey;
}[] = [
  {
    symptomKey: 'docs.verify.error1.symptom',
    reasonKey: 'docs.verify.error1.reason',
    actionKey: 'docs.verify.error1.action',
  },
  {
    symptomKey: 'docs.verify.error2.symptom',
    reasonKey: 'docs.verify.error2.reason',
    actionKey: 'docs.verify.error2.action',
  },
  {
    symptomKey: 'docs.verify.error3.symptom',
    reasonKey: 'docs.verify.error3.reason',
    actionKey: 'docs.verify.error3.action',
  },
  {
    symptomKey: 'docs.verify.error4.symptom',
    reasonKey: 'docs.verify.error4.reason',
    actionKey: 'docs.verify.error4.action',
  },
  {
    symptomKey: 'docs.verify.error5.symptom',
    reasonKey: 'docs.verify.error5.reason',
    actionKey: 'docs.verify.error5.action',
  },
  {
    symptomKey: 'docs.verify.error6.symptom',
    reasonKey: 'docs.verify.error6.reason',
    actionKey: 'docs.verify.error6.action',
  },
  {
    symptomKey: 'docs.verify.error7.symptom',
    reasonKey: 'docs.verify.error7.reason',
    actionKey: 'docs.verify.error7.action',
  },
];

/**
 * Canonical `namespace/model` names for one vendor, from the catalog
 * SKU market. Null while the catalog has no data so the caller can fall
 * back to the illustrative literals.
 */
function vendorSkuNames(
  catalog: CatalogResponse | undefined,
  vendor: string,
): readonly string[] | null {
  if (catalog?.skus === undefined) return null;
  const names = catalog.skus.filter((sku) => sku.vendor === vendor).map((sku) => sku.sku_name);
  return names.length > 0 ? names : null;
}

export function HandwrittenDocs(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const catalogQ = useCatalog();
  const [platform, setPlatform] = useState<DocsPlatform>('macos');
  const activeSection = useActiveSection();

  const anthropicBase = getGatewayBaseUrl();
  const openaiBase = `${anthropicBase}/v1`;
  const consoleOrigin = typeof window !== 'undefined' ? window.location.origin : '';

  const anthropicSkuNames = vendorSkuNames(catalogQ.data, 'anthropic') ?? FALLBACK_ANTHROPIC_MODELS;
  const openaiSkuNames = vendorSkuNames(catalogQ.data, 'openai') ?? FALLBACK_OPENAI_MODELS;
  const claudeModels = anthropicSkuNames.join(' / ');
  const gptModels = openaiSkuNames.join(' / ');
  const exampleAnthropicModel = anthropicSkuNames[0] ?? EXAMPLE_ANTHROPIC_MODEL;
  const exampleOpenaiModel = openaiSkuNames[0] ?? EXAMPLE_OPENAI_MODEL;
  // The catalog carries no image marker a tenant key can read — the metering
  // profile is not tenant-visible — so the example falls back to the name, in
  // the conservative direction: a wrong guess shows an illustrative literal,
  // never a text SKU presented as an image one.
  const exampleImageModel =
    openaiSkuNames.find((name) => name.toLowerCase().includes('image')) ?? EXAMPLE_IMAGE_MODEL;
  // Same conservative name-match as images: the catalog has no tenant-visible
  // video marker, so a miss shows the illustrative literal rather than a text
  // SKU presented as a Grok video one. Search the whole catalog — the SKU
  // ships under the `xai/` namespace, not the openai vendor slice.
  const exampleVideoModel =
    catalogQ.data?.skus?.find((sku) => sku.sku_name.toLowerCase().includes('grok-imagine-video'))
      ?.sku_name ?? EXAMPLE_VIDEO_MODEL;
  // Same conservative name-match as videos: the catalog has no tenant-visible
  // seedance marker, so a miss shows the illustrative literal rather than a
  // text SKU presented as a Seedance one. Search the whole catalog — hidden
  // listings still match once they appear on this tenant's catalog.
  const exampleSeedanceModel =
    catalogQ.data?.skus?.find((sku) => sku.sku_name.toLowerCase().includes('seedance'))?.sku_name ??
    EXAMPLE_SEEDANCE_MODEL;

  async function copy(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('docs.toast.copied'));
    } catch (err) {
      toast.error(localizeApiError(err, t));
    }
  }

  const onCopy = (text: string) => () => {
    void copy(text);
  };

  return (
    <section data-testid="docs-page" className="flex flex-col gap-4">
      <PageHeader title={t('docs.title')} sub={t('docs.subtitle')} testId="docs-header" />

      <EndpointCards />

      {/* Below xl: TOC as a horizontal anchor chip row. */}
      <nav
        aria-label={t('docs.toc.aria')}
        data-testid="docs-toc-chips"
        className="flex flex-wrap gap-1.5 xl:hidden"
      >
        {SECTIONS.map((section) => {
          const isActive = section.id === activeSection;
          return (
            <a
              key={section.id}
              href={`#${section.id}`}
              aria-current={isActive ? 'true' : undefined}
              data-testid={`docs-toc-chip-${section.id}`}
              className={cx(
                buttonClass(isActive ? 'primary' : 'secondary', 'sm'),
                // The chip row scrolls horizontally on narrow viewports, so the
                // active chip can sit off-screen. Same reason as the sidebar.
                isActive && 'scroll-mx-3',
              )}
            >
              {t(section.labelKey)}
            </a>
          );
        })}
      </nav>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[11rem_minmax(0,1fr)]">
        {/* xl+: sticky sidebar TOC. */}
        <aside className="hidden xl:block">
          <nav
            aria-label={t('docs.toc.aria')}
            data-testid="docs-toc"
            className="sticky top-4 flex flex-col gap-0.5 rounded-lg border border-line-soft bg-card p-1.5"
          >
            {SECTIONS.map((section) => {
              const isActive = section.id === activeSection;
              return (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  // Same active affordance as the shell's own nav
                  // (`bg-forest-tint text-forest-deep`) — one convention for
                  // "you are here" across the console, learned once. The lamp
                  // carries the state in a second channel (DESIGN.md §9).
                  aria-current={isActive ? 'true' : undefined}
                  data-testid={`docs-toc-item-${section.id}`}
                  className={cx(
                    'flex items-center gap-2 rounded px-2.5 py-2.5 text-label font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40',
                    isActive
                      ? 'bg-forest-tint text-forest-deep'
                      : 'text-ink-mid hover:bg-active/50 hover:text-ink',
                  )}
                >
                  <span className="min-w-0 flex-1 truncate">{t(section.labelKey)}</span>
                  {isActive ? <StatusLamp tone="rust" size="sm" /> : null}
                </a>
              );
            })}
          </nav>
        </aside>

        <div className="flex min-w-0 flex-col gap-4">
          {/* ── Prepare ─────────────────────────────────────────── */}
          <DocsSection id="prepare" title={t('docs.section.prepare.title')}>
            <KeyValueList
              rows={[
                { label: t('docs.prepare.console'), value: consoleOrigin, mono: true },
                {
                  label: t('docs.prepare.keys_page'),
                  value: `${consoleOrigin}/keys`,
                  mono: true,
                },
                { label: t('docs.prepare.models'), value: 'GET /v1/models', mono: true },
                {
                  label: t('docs.prepare.requests'),
                  value: `${consoleOrigin}/requests`,
                  mono: true,
                },
              ]}
            />
            <Callout tone="warn">{t('docs.prepare.warning')}</Callout>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <MappingCard
                supplier="anthropic"
                channel={t('docs.prepare.anthropic_entry')}
                models={claudeModels}
                fit={t('docs.prepare.anthropic_fit')}
                testId="docs-mapping-anthropic"
              />
              <MappingCard
                supplier="openai"
                channel={t('docs.prepare.openai_entry')}
                models={gptModels}
                fit={t('docs.prepare.openai_fit')}
                testId="docs-mapping-openai"
              />
            </div>
            <SnippetCard
              title={t('docs.prepare.env_check')}
              code={ENV_CHECK_SNIPPET}
              copyLabel={t('docs.copy')}
              onCopy={onCopy(ENV_CHECK_SNIPPET)}
            />
          </DocsSection>

          {/* ── Model names + mappings ──────────────────────────── */}
          <DocsSection id="models" title={t('docs.section.models.title')}>
            <p className="m-0 text-body leading-relaxed text-ink-mid">
              {t('docs.models.intro', { example: exampleAnthropicModel })}
            </p>
            <KeyValueList
              rows={[
                {
                  label: t('docs.models.row.canonical'),
                  value: t('docs.models.row.canonical_value'),
                },
                {
                  label: t('docs.models.row.alias'),
                  value: t('docs.models.row.alias_value'),
                },
                {
                  label: t('docs.models.row.manage'),
                  value: t('docs.models.row.manage_value'),
                },
                {
                  label: t('docs.models.row.pricing'),
                  value: t('docs.models.row.pricing_value'),
                },
              ]}
            />
            <SnippetCard
              title={t('docs.models.list_snippet')}
              code={modelsCurlSnippet(anthropicBase)}
              copyLabel={t('docs.copy')}
              onCopy={onCopy(modelsCurlSnippet(anthropicBase))}
              testId="docs-snippet-models-curl"
            />
            <Callout>{t('docs.models.source_of_truth')}</Callout>
            <Callout tone="warn">{t('docs.models.cc_lane')}</Callout>
          </DocsSection>

          {/* ── Create key ──────────────────────────────────────── */}
          <DocsSection id="create-key" title={t('docs.section.createKey.title')}>
            <ol className="m-0 grid list-none grid-cols-1 gap-3 p-0 md:grid-cols-3">
              <NumberedStep n={1} title={t('docs.createKey.step1.title')}>
                {t('docs.createKey.step1.body')}
              </NumberedStep>
              <NumberedStep n={2} title={t('docs.createKey.step2.title')}>
                {t('docs.createKey.step2.body')}
              </NumberedStep>
              <NumberedStep n={3} title={t('docs.createKey.step3.title')}>
                {t('docs.createKey.step3.body')}
              </NumberedStep>
            </ol>
            <KeyValueList
              rows={[
                {
                  label: t('docs.createKey.row.name'),
                  value: t('docs.createKey.row.name_value'),
                },
                {
                  label: t('docs.createKey.row.template'),
                  value: t('docs.createKey.row.template_value'),
                },
                {
                  label: t('docs.createKey.row.limits'),
                  value: t('docs.createKey.row.limits_value'),
                },
              ]}
            />
            <Callout>{t('docs.createKey.hint')}</Callout>
            <Link
              to="/keys"
              className={cx(buttonClass('secondary'), 'w-fit')}
              data-testid="docs-open-keys"
            >
              {t('docs.createKey.open_keys')}
            </Link>
          </DocsSection>

          {/* ── Claude Code ─────────────────────────────────────── */}
          <DocsSection id="claude-code" title={t('docs.section.claudeCode.title')}>
            <p className="m-0 text-body leading-relaxed text-ink-mid">
              {t('docs.claudeCode.intro')}
            </p>
            <SnippetCard
              title={t('docs.claudeCode.install')}
              code={CLAUDE_CODE_INSTALL_SNIPPET}
              copyLabel={t('docs.copy')}
              onCopy={onCopy(CLAUDE_CODE_INSTALL_SNIPPET)}
            />
            <PlatformTabs value={platform} onChange={setPlatform} />
            <SnippetCard
              title={t('docs.claudeCode.temp_env')}
              code={claudeTempEnvSnippet(platform, anthropicBase, exampleAnthropicModel)}
              copyLabel={t('docs.copy')}
              onCopy={onCopy(claudeTempEnvSnippet(platform, anthropicBase, exampleAnthropicModel))}
              testId="docs-snippet-claude-temp-env"
            />
            <SnippetCard
              title={t('docs.claudeCode.long_term')}
              code={claudeLongTermSnippet(platform, anthropicBase, exampleAnthropicModel)}
              copyLabel={t('docs.copy')}
              onCopy={onCopy(claudeLongTermSnippet(platform, anthropicBase, exampleAnthropicModel))}
            />
            <KeyValueList
              rows={[
                {
                  label: t('docs.claudeCode.row.model'),
                  value: t('docs.claudeCode.row.model_value'),
                },
                {
                  label: t('docs.claudeCode.row.first_test'),
                  value: t('docs.claudeCode.row.first_test_value'),
                },
                {
                  label: t('docs.claudeCode.row.prompt'),
                  value: t('docs.claudeCode.row.prompt_value'),
                },
                {
                  label: t('docs.claudeCode.row.url_mistake'),
                  value: t('docs.claudeCode.row.url_mistake_value'),
                },
              ]}
            />
            <SnippetCard
              title={t('docs.claudeCode.disable_betas')}
              code={disableClaudeBetasSnippet(platform)}
              copyLabel={t('docs.copy')}
              onCopy={onCopy(disableClaudeBetasSnippet(platform))}
            />
          </DocsSection>

          {/* ── Codex ───────────────────────────────────────────── */}
          <DocsSection id="codex" title={t('docs.section.codex.title')}>
            <p className="m-0 text-body leading-relaxed text-ink-mid">
              {t('docs.codex.intro', { example: exampleOpenaiModel })}
            </p>
            {/* 2026-08-25：原先这里是协议切换（Anthropic/OpenAI Tabs），其
                Anthropic 分支把「Claude Code 接入」已有的环境变量重复塞在
                Codex 标题下（用户实拍反馈误导性强），已删。按用户拍板改放
                平台切换（macOS / Linux｜Windows），与 Claude Code 节同款，
                切的是下方环境变量的 shell 写法；两节共享同一 platform 状态。 */}
            <PlatformTabs value={platform} onChange={setPlatform} testIdSuffix="-codex" />
            <SnippetCard
              title={t('docs.codex.env.codex')}
              code={codexEnvSnippet(platform, openaiBase)}
              copyLabel={t('docs.copy')}
              onCopy={onCopy(codexEnvSnippet(platform, openaiBase))}
              testId="docs-snippet-codex-env"
            />
            <SnippetCard
              title="~/.codex/config.toml"
              code={codexConfigSnippet(openaiBase, exampleOpenaiModel)}
              copyLabel={t('docs.copy')}
              onCopy={onCopy(codexConfigSnippet(openaiBase, exampleOpenaiModel))}
            />
            <KeyValueList
              rows={[
                {
                  label: t('docs.codex.row.provider'),
                  value: t('docs.codex.row.provider_value'),
                },
                { label: t('docs.codex.row.base_url'), value: openaiBase, mono: true },
                {
                  label: t('docs.codex.row.api_key'),
                  value: t('docs.codex.row.api_key_value'),
                },
                { label: t('docs.codex.row.model'), value: gptModels, mono: true },
              ]}
            />
          </DocsSection>

          {/* ── CC Switch ───────────────────────────────────────── */}
          <DocsSection id="cc-switch" title={t('docs.section.ccSwitch.title')}>
            <p className="m-0 text-body leading-relaxed text-ink-mid">{t('docs.ccSwitch.intro')}</p>
            <SnippetCard
              title={t('docs.ccSwitch.install')}
              code={CC_SWITCH_INSTALL_SNIPPET}
              copyLabel={t('docs.copy')}
              onCopy={onCopy(CC_SWITCH_INSTALL_SNIPPET)}
            />
            <div className="grid grid-cols-1 gap-3 2xl:grid-cols-2">
              <ProviderPanel
                title={t('docs.ccSwitch.claude_panel')}
                rows={[
                  {
                    label: t('docs.ccSwitch.row.provider_name'),
                    value: t('docs.ccSwitch.row.claude_provider_name'),
                  },
                  {
                    label: t('docs.ccSwitch.row.app'),
                    value: t('docs.ccSwitch.row.claude_app'),
                  },
                  {
                    label: t('docs.ccSwitch.row.protocol'),
                    value: t('docs.ccSwitch.row.claude_protocol'),
                  },
                  { label: t('docs.ccSwitch.row.base_url'), value: anthropicBase, mono: true },
                  {
                    label: t('docs.ccSwitch.row.api_key'),
                    value: t('docs.ccSwitch.row.claude_key'),
                  },
                  { label: t('docs.ccSwitch.row.model'), value: claudeModels, mono: true },
                ]}
              />
              <ProviderPanel
                title={t('docs.ccSwitch.openai_panel')}
                rows={[
                  {
                    label: t('docs.ccSwitch.row.provider_name'),
                    value: t('docs.ccSwitch.row.openai_provider_name'),
                  },
                  {
                    label: t('docs.ccSwitch.row.app'),
                    value: t('docs.ccSwitch.row.openai_app'),
                  },
                  {
                    label: t('docs.ccSwitch.row.protocol'),
                    value: t('docs.ccSwitch.row.openai_protocol'),
                  },
                  { label: t('docs.ccSwitch.row.base_url'), value: openaiBase, mono: true },
                  {
                    label: t('docs.ccSwitch.row.api_key'),
                    value: t('docs.ccSwitch.row.openai_key'),
                  },
                  { label: t('docs.ccSwitch.row.model'), value: gptModels, mono: true },
                ]}
              />
            </div>
            <Checklist
              title={t('docs.ccSwitch.checklist.title')}
              items={[
                t('docs.ccSwitch.checklist.1'),
                t('docs.ccSwitch.checklist.2'),
                t('docs.ccSwitch.checklist.3'),
                t('docs.ccSwitch.checklist.4'),
                t('docs.ccSwitch.checklist.5'),
              ]}
            />
          </DocsSection>

          {/* ── OpenClaw ────────────────────────────────────────── */}
          <DocsSection id="openclaw" title={t('docs.section.openclaw.title')}>
            <p className="m-0 text-body leading-relaxed text-ink-mid">{t('docs.openclaw.intro')}</p>
            <SnippetCard
              title={t('docs.openclaw.snippet')}
              code={openclawProviderSnippet(openaiBase, exampleOpenaiModel)}
              copyLabel={t('docs.copy')}
              onCopy={onCopy(openclawProviderSnippet(openaiBase, exampleOpenaiModel))}
            />
            <KeyValueList
              rows={[
                { label: t('docs.openclaw.row.base_url'), value: openaiBase, mono: true },
                {
                  label: t('docs.openclaw.row.api_key'),
                  value: t('docs.openclaw.row.api_key_value'),
                },
                { label: t('docs.openclaw.row.model'), value: gptModels, mono: true },
                {
                  label: t('docs.openclaw.row.verify'),
                  value: t('docs.openclaw.row.verify_value'),
                },
              ]}
            />
          </DocsSection>

          {/* ── Graphical clients ───────────────────────────────── */}
          <DocsSection id="clients" title={t('docs.section.clients.title')}>
            <Table testId="docs-tools-table">
              <THead>
                <Tr interactive={false}>
                  <Th>{t('docs.clients.column.tool')}</Th>
                  <Th>{t('docs.clients.column.vendor')}</Th>
                  <Th>{t('docs.clients.column.base')}</Th>
                  <Th>{t('docs.clients.column.notes')}</Th>
                </Tr>
              </THead>
              <TBody>
                {TOOL_GUIDES.map((tool) => (
                  <Tr key={tool.name} interactive={false}>
                    <Td emphasis heading>
                      {tool.name}
                    </Td>
                    <Td mono label={t('docs.clients.column.vendor')}>
                      {tool.supplier === 'mixed' ? 'anthropic / openai' : tool.supplier}
                    </Td>
                    <Td
                      mono
                      label={t('docs.clients.column.base')}
                      className="max-w-56 truncate max-md:max-w-none"
                    >
                      {tool.base === 'both'
                        ? t('docs.clients.base_both')
                        : tool.base === 'anthropic'
                          ? anthropicBase
                          : openaiBase}
                    </Td>
                    <Td label={t('docs.clients.column.notes')} stack>
                      {t(tool.detailKey)}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
            <div className="grid grid-cols-1 gap-3 2xl:grid-cols-3">
              {CLIENT_GUIDES.map((guide) => (
                <article
                  key={guide.name}
                  className="rounded-lg border border-line-soft bg-well p-4"
                  data-testid={`docs-client-${guide.keyName}`}
                >
                  <h3 className="m-0 text-body font-medium text-ink">{guide.name}</h3>
                  <p className="m-0 mt-1 text-label text-ink-low">
                    {t('docs.clients.key_name', { name: guide.keyName })}
                  </p>
                  <KeyValueList
                    compact
                    rows={[
                      { label: t('docs.clients.row.entry'), value: t(guide.entryKey) },
                      {
                        label: t('docs.clients.row.provider_type'),
                        value: t('docs.clients.row.provider_type_value'),
                      },
                      { label: t('docs.clients.row.api_host'), value: openaiBase, mono: true },
                      { label: t('docs.clients.row.model'), value: gptModels, mono: true },
                      { label: t('docs.clients.row.test'), value: t(guide.testKey) },
                    ]}
                  />
                  <p className="m-0 mt-3 text-label leading-relaxed text-ink-low">
                    {t(guide.noteKey)}
                  </p>
                </article>
              ))}
            </div>
            <SnippetCard
              title={t('docs.clients.lobechat_env')}
              code={lobechatEnvSnippet(openaiBase)}
              copyLabel={t('docs.copy')}
              onCopy={onCopy(lobechatEnvSnippet(openaiBase))}
            />
          </DocsSection>

          {/* ── Claude Desktop ──────────────────────────────────── */}
          <DocsSection id="claude-desktop" title={t('docs.section.claudeDesktop.title')}>
            <p className="m-0 text-body leading-relaxed text-ink-mid">
              {t('docs.claudeDesktop.intro')}
            </p>
            <div className="grid grid-cols-1 gap-3 2xl:grid-cols-2">
              <Checklist
                title={t('docs.claudeDesktop.macos.title')}
                items={[
                  t('docs.claudeDesktop.macos.1'),
                  t('docs.claudeDesktop.macos.2'),
                  t('docs.claudeDesktop.macos.3'),
                  t('docs.claudeDesktop.macos.4', { baseUrl: anthropicBase }),
                  t('docs.claudeDesktop.macos.5'),
                ]}
              />
              <Checklist
                title={t('docs.claudeDesktop.windows.title')}
                items={[
                  t('docs.claudeDesktop.windows.1'),
                  t('docs.claudeDesktop.windows.2'),
                  t('docs.claudeDesktop.windows.3'),
                  t('docs.claudeDesktop.windows.4', { baseUrl: anthropicBase }),
                  t('docs.claudeDesktop.windows.5'),
                ]}
              />
            </div>
            <KeyValueList
              rows={[
                {
                  label: t('docs.claudeDesktop.row.no_menu'),
                  value: t('docs.claudeDesktop.row.no_menu_value'),
                },
                {
                  label: t('docs.claudeDesktop.row.no_gateway'),
                  value: t('docs.claudeDesktop.row.no_gateway_value'),
                },
                {
                  label: t('docs.claudeDesktop.row.no_connect'),
                  value: t('docs.claudeDesktop.row.no_connect_value'),
                },
                {
                  label: t('docs.claudeDesktop.row.rejected'),
                  value: t('docs.claudeDesktop.row.rejected_value'),
                },
              ]}
            />
          </DocsSection>

          {/* ── VS Code ─────────────────────────────────────────── */}
          <DocsSection id="vscode" title={t('docs.section.vscode.title')}>
            <p className="m-0 text-body leading-relaxed text-ink-mid">{t('docs.vscode.intro')}</p>
            <SnippetCard
              title={t('docs.vscode.snippet')}
              code={claudeTempEnvSnippet(platform, anthropicBase, exampleAnthropicModel)}
              copyLabel={t('docs.copy')}
              onCopy={onCopy(claudeTempEnvSnippet(platform, anthropicBase, exampleAnthropicModel))}
            />
            <KeyValueList
              rows={[
                { label: t('docs.vscode.row.openai_base'), value: openaiBase, mono: true },
                {
                  label: t('docs.vscode.row.anthropic_base'),
                  value: anthropicBase,
                  mono: true,
                },
                {
                  label: t('docs.vscode.row.openai_models'),
                  value: t('docs.vscode.row.openai_models_value'),
                },
                {
                  label: t('docs.vscode.row.anthropic_models'),
                  value: t('docs.vscode.row.anthropic_models_value'),
                },
              ]}
            />
          </DocsSection>

          {/* ── Images ──────────────────────────────────────────
              The OpenAI Images protocol, served by a capability that is a
              built-in tool of a Responses endpoint. That asymmetry is
              invisible to a caller — the request and response are the
              standard shapes — but three of its consequences are NOT, and a
              tenant who does not know them reads correct behaviour as a bug:
              `size` and `quality` are advisory, one call yields one image,
              and the model may decline. Documented here rather than
              discovered in production. */}
          <DocsSection id="images" title={t('docs.section.images.title')}>
            <Callout>{t('docs.images.note')}</Callout>
            {/* Both snippets carry generate AND edit, so the two cards are the
                same two calls in two languages — equal weight, and the split
                is the reader's choice of tool rather than a split of the
                feature across cards. */}
            <div className="grid grid-cols-1 gap-3 2xl:grid-cols-2">
              <SnippetCard
                title={t('docs.images.sdk')}
                code={imagesSdkSnippet(openaiBase, exampleImageModel)}
                copyLabel={t('docs.copy')}
                onCopy={onCopy(imagesSdkSnippet(openaiBase, exampleImageModel))}
                testId="docs-snippet-images-sdk"
              />
              <SnippetCard
                title={t('docs.images.curl')}
                code={imagesCurlBothSnippet(openaiBase, exampleImageModel)}
                copyLabel={t('docs.copy')}
                onCopy={onCopy(imagesCurlBothSnippet(openaiBase, exampleImageModel))}
                testId="docs-snippet-images-curl"
              />
            </div>
            <ImageParamTable
              caption={t('docs.images.table.generate')}
              rows={IMAGE_GENERATE_PARAMS}
              testId="docs-images-generate-params-table"
            />
            <ImageParamTable
              caption={t('docs.images.table.edit')}
              rows={IMAGE_EDIT_PARAMS}
              testId="docs-images-edit-params-table"
            />
            <Callout>{t('docs.images.billing')}</Callout>
          </DocsSection>

          {/* ── Videos ──────────────────────────────────────────
              The xAI video protocol as this gateway actually wires it:
              create + poll, 480p/720p only, image-to-video and
              reference audio/voice still unwired, reference_images
              billed per image on top of delivered seconds. Documented
              from the route, not from the official surface's knobs. */}
          <DocsSection id="videos" title={t('docs.section.videos.title')}>
            <Callout>{t('docs.videos.note')}</Callout>
            <div className="grid grid-cols-1 gap-3 2xl:grid-cols-2">
              <SnippetCard
                title={t('docs.videos.sdk')}
                code={videosSdkSnippet(anthropicBase, exampleVideoModel)}
                copyLabel={t('docs.copy')}
                onCopy={onCopy(videosSdkSnippet(anthropicBase, exampleVideoModel))}
                testId="docs-snippet-videos-sdk"
              />
              <SnippetCard
                title={t('docs.videos.curl')}
                code={videosCurlBothSnippet(openaiBase, exampleVideoModel)}
                copyLabel={t('docs.copy')}
                onCopy={onCopy(videosCurlBothSnippet(openaiBase, exampleVideoModel))}
                testId="docs-snippet-videos-curl"
              />
            </div>
            <VideoParamTable
              caption={t('docs.videos.table.generate')}
              rows={VIDEO_GENERATE_PARAMS}
              testId="docs-videos-generate-params-table"
            />
            <Callout>{t('docs.videos.poll')}</Callout>
            <Callout>{t('docs.videos.billing')}</Callout>
          </DocsSection>

          {/* ── Seedance ────────────────────────────────────────
              Ark task protocol as this gateway wires it: submit
              POST /v1/tasks/submit, poll GET /v1/tasks/status,
              capitalized statuses, output.urls[] as authenticated
              handles. Documented from measurement, not from the
              vendor surface. Hidden release — copy describes the
              protocol and does not imply the reader has access. */}
          <DocsSection id="seedance" title={t('docs.section.seedance.title')}>
            <Callout>{t('docs.seedance.note')}</Callout>
            <div className="grid grid-cols-1 gap-3 2xl:grid-cols-2">
              <SnippetCard
                title={t('docs.seedance.python')}
                code={seedancePythonSnippet(openaiBase, exampleSeedanceModel)}
                copyLabel={t('docs.copy')}
                onCopy={onCopy(seedancePythonSnippet(openaiBase, exampleSeedanceModel))}
                testId="docs-snippet-seedance-python"
              />
              <SnippetCard
                title={t('docs.seedance.curl')}
                code={seedanceCurlBothSnippet(openaiBase, exampleSeedanceModel)}
                copyLabel={t('docs.copy')}
                onCopy={onCopy(seedanceCurlBothSnippet(openaiBase, exampleSeedanceModel))}
                testId="docs-snippet-seedance-curl"
              />
            </div>
            <SeedanceParamTable
              caption={t('docs.seedance.table.params')}
              rows={SEEDANCE_SUBMIT_PARAMS}
              testId="docs-seedance-params-table"
            />
            <Callout>{t('docs.seedance.reference')}</Callout>
            <div className="grid grid-cols-1 gap-3 2xl:grid-cols-2">
              <SnippetCard
                title={t('docs.seedance.reference.image')}
                code={SEEDANCE_REFERENCE_IMAGE_SNIPPET}
                copyLabel={t('docs.copy')}
                onCopy={onCopy(SEEDANCE_REFERENCE_IMAGE_SNIPPET)}
                testId="docs-snippet-seedance-image"
              />
              <SnippetCard
                title={t('docs.seedance.reference.video')}
                code={SEEDANCE_REFERENCE_VIDEO_SNIPPET}
                copyLabel={t('docs.copy')}
                onCopy={onCopy(SEEDANCE_REFERENCE_VIDEO_SNIPPET)}
                testId="docs-snippet-seedance-video"
              />
            </div>
            <Callout>{t('docs.seedance.artifacts')}</Callout>
            <Callout>{t('docs.seedance.billing')}</Callout>
          </DocsSection>

          {/* ── Verify + troubleshoot ───────────────────────────── */}
          <DocsSection id="verify" title={t('docs.section.verify.title')}>
            <Callout>{t('docs.verify.note')}</Callout>
            <div className="grid grid-cols-1 gap-3 2xl:grid-cols-2">
              {/* The Anthropic example keeps a placeholder model on purpose:
                  the catalog carries no marker for which Claude namespaces
                  accept plain-API traffic, so any concrete default could 5xx
                  under curl (Claude Code-only namespaces). */}
              <SnippetCard
                title={t('docs.verify.anthropic_curl')}
                code={anthropicCurlSnippet(anthropicBase)}
                copyLabel={t('docs.copy')}
                onCopy={onCopy(anthropicCurlSnippet(anthropicBase))}
                testId="docs-snippet-anthropic-curl"
              />
              <SnippetCard
                title={t('docs.verify.openai_curl')}
                code={openaiCurlSnippet(openaiBase, exampleOpenaiModel)}
                copyLabel={t('docs.copy')}
                onCopy={onCopy(openaiCurlSnippet(openaiBase, exampleOpenaiModel))}
              />
            </div>
            <Table testId="docs-errors-table">
              <THead>
                <Tr interactive={false}>
                  <Th>{t('docs.verify.column.symptom')}</Th>
                  <Th>{t('docs.verify.column.reason')}</Th>
                  <Th>{t('docs.verify.column.action')}</Th>
                </Tr>
              </THead>
              <TBody>
                {VERIFY_ERRORS.map((row) => (
                  <Tr key={row.symptomKey} interactive={false}>
                    <Td emphasis heading>
                      {t(row.symptomKey)}
                    </Td>
                    <Td label={t('docs.verify.column.reason')} stack>
                      {t(row.reasonKey)}
                    </Td>
                    <Td label={t('docs.verify.column.action')} stack>
                      {t(row.actionKey)}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <ActionLink
                to="/usage"
                label={t('docs.verify.link.usage')}
                detail={t('docs.verify.link.usage_detail')}
                testId="docs-link-usage"
              />
              <ActionLink
                to="/requests"
                label={t('docs.verify.link.requests')}
                detail={t('docs.verify.link.requests_detail')}
                testId="docs-link-requests"
              />
              <ActionLink
                to="/pricing"
                label={t('docs.verify.link.pricing')}
                detail={t('docs.verify.link.pricing_detail')}
                testId="docs-link-pricing"
              />
            </div>
            <Callout>{t('docs.verify.security')}</Callout>
          </DocsSection>
        </div>
      </div>
    </section>
  );
}

/* ── Local presentational helpers (Field Notes tokens only) ──────── */

/**
 * One route's parameter table. Shared by `generations` and `edits` so the two
 * cannot drift into different column sets — the whole reason for splitting
 * them was that a merged table hid which parameter belonged to which route.
 */
function SeedanceParamTable({
  caption,
  rows,
  testId,
}: {
  readonly caption: string;
  readonly rows: readonly SeedanceParamRow[];
  readonly testId: string;
}): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-col gap-2">
      <p className="m-0 text-body font-medium text-ink">{caption}</p>
      <Table testId={testId}>
        <THead>
          <Tr interactive={false}>
            <Th>{t('docs.seedance.column.param')}</Th>
            <Th>{t('docs.seedance.column.required')}</Th>
            <Th>{t('docs.seedance.column.default')}</Th>
            <Th>{t('docs.seedance.column.behaviour')}</Th>
          </Tr>
        </THead>
        <TBody>
          {rows.map((row) => (
            <Tr key={row.param} interactive={false}>
              <Td emphasis heading mono>
                {row.param}
              </Td>
              <Td label={t('docs.seedance.column.required')} stack>
                {row.required ? t('docs.seedance.required.yes') : t('docs.seedance.required.no')}
              </Td>
              <Td
                label={t('docs.seedance.column.default')}
                mono={row.defaultValue !== undefined}
                stack
              >
                {row.defaultValue ?? t('docs.seedance.default.none')}
              </Td>
              <Td label={t('docs.seedance.column.behaviour')} stack>
                {t(row.behaviourKey)}
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  );
}

function VideoParamTable({
  caption,
  rows,
  testId,
}: {
  readonly caption: string;
  readonly rows: readonly VideoParamRow[];
  readonly testId: string;
}): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-col gap-2">
      <p className="m-0 text-body font-medium text-ink">{caption}</p>
      <Table testId={testId}>
        <THead>
          <Tr interactive={false}>
            <Th>{t('docs.videos.column.param')}</Th>
            <Th>{t('docs.videos.column.required')}</Th>
            <Th>{t('docs.videos.column.default')}</Th>
            <Th>{t('docs.videos.column.behaviour')}</Th>
          </Tr>
        </THead>
        <TBody>
          {rows.map((row) => (
            <Tr key={row.param} interactive={false}>
              <Td emphasis heading mono>
                {row.param}
              </Td>
              <Td label={t('docs.videos.column.required')} stack>
                {row.required ? t('docs.videos.required.yes') : t('docs.videos.required.no')}
              </Td>
              <Td
                label={t('docs.videos.column.default')}
                mono={row.defaultValue !== undefined}
                stack
              >
                {row.defaultValue ?? t('docs.videos.default.none')}
              </Td>
              <Td label={t('docs.videos.column.behaviour')} stack>
                {t(row.behaviourKey)}
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  );
}

function ImageParamTable({
  caption,
  rows,
  testId,
}: {
  readonly caption: string;
  readonly rows: readonly ImageParamRow[];
  readonly testId: string;
}): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-col gap-2">
      <p className="m-0 text-body font-medium text-ink">{caption}</p>
      <Table testId={testId}>
        <THead>
          <Tr interactive={false}>
            <Th>{t('docs.images.column.param')}</Th>
            <Th>{t('docs.images.column.required')}</Th>
            <Th>{t('docs.images.column.default')}</Th>
            <Th>{t('docs.images.column.behaviour')}</Th>
          </Tr>
        </THead>
        <TBody>
          {rows.map((row) => (
            <Tr key={row.param} interactive={false}>
              <Td emphasis heading mono>
                {row.param}
              </Td>
              <Td label={t('docs.images.column.required')} stack>
                {row.required ? t('docs.images.required.yes') : t('docs.images.required.no')}
              </Td>
              <Td
                label={t('docs.images.column.default')}
                mono={row.defaultValue !== undefined}
                stack
              >
                {row.defaultValue ?? t('docs.images.default.none')}
              </Td>
              <Td label={t('docs.images.column.behaviour')} stack>
                {t(row.behaviourKey)}
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  );
}

function DocsSection({
  id,
  title,
  children,
}: {
  readonly id: SectionId;
  readonly title: string;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <div id={id} className="scroll-mt-4" data-testid={`docs-section-${id}`}>
      <Panel caption={title} padded>
        <div className="flex flex-col gap-4">{children}</div>
      </Panel>
    </div>
  );
}

function SnippetCard({
  title,
  code,
  copyLabel,
  onCopy,
  testId,
}: {
  readonly title: string;
  readonly code: string;
  readonly copyLabel: string;
  readonly onCopy: () => void;
  readonly testId?: string;
}): JSX.Element {
  return (
    <article
      data-testid={testId}
      className="min-w-0 overflow-hidden rounded-lg border border-line-soft bg-well"
    >
      <div className="flex items-center justify-between gap-3 border-b border-line-soft px-3 py-1.5">
        <h3 className="m-0 text-label font-medium text-ink-mid">{title}</h3>
        <Button variant="ghost" size="sm" onClick={onCopy}>
          <Icon name="content_copy" size="xs" />
          {copyLabel}
        </Button>
      </div>
      <pre className="m-0 overflow-x-auto px-3 py-2.5 font-mono text-body leading-relaxed text-ink">
        {code}
      </pre>
    </article>
  );
}

function KeyValueList({
  rows,
  compact = false,
}: {
  readonly rows: readonly {
    readonly label: string;
    readonly value: string;
    readonly mono?: boolean;
  }[];
  readonly compact?: boolean;
}): JSX.Element {
  return (
    <dl
      className={cx(
        'm-0 divide-y divide-line-soft',
        compact ? 'mt-3' : 'rounded-lg border border-line-soft',
      )}
    >
      {rows.map((row) => (
        <div
          key={`${row.label}:${row.value}`}
          className="grid grid-cols-1 gap-1 px-3 py-2 md:grid-cols-[9rem_minmax(0,1fr)]"
        >
          <dt className="text-label text-ink-low">{row.label}</dt>
          <dd
            className={cx(
              'm-0 min-w-0 break-words text-body text-ink-mid',
              row.mono === true && 'font-mono',
            )}
          >
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function Callout({
  children,
  tone = 'neutral',
}: {
  readonly children: ReactNode;
  readonly tone?: 'neutral' | 'warn';
}): JSX.Element {
  return (
    <div
      className={cx(
        'rounded-lg border px-4 py-3 text-body leading-relaxed',
        tone === 'warn'
          ? 'border-warn-line bg-warn-tint text-warn'
          : 'border-line-soft bg-well text-ink-mid',
      )}
    >
      {children}
    </div>
  );
}

function NumberedStep({
  n,
  title,
  children,
}: {
  readonly n: number;
  readonly title: string;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <li className="flex gap-3 rounded-lg border border-line-soft bg-well p-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-raised font-mono text-label text-forest">
        {n}
      </span>
      <span className="min-w-0">
        <strong className="block text-body font-medium text-ink">{title}</strong>
        <span className="text-body leading-relaxed text-ink-mid">{children}</span>
      </span>
    </li>
  );
}

function MappingCard({
  supplier,
  channel,
  models,
  fit,
  testId,
}: {
  readonly supplier: string;
  readonly channel: string;
  readonly models: string;
  readonly fit: string;
  readonly testId?: string;
}): JSX.Element {
  return (
    <article data-testid={testId} className="rounded-lg border border-line-soft bg-well p-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="m-0 font-mono text-body font-medium text-forest">{supplier}</h3>
        <span className="rounded-full border border-line px-2 py-0.5 text-caption text-ink-low">
          {channel}
        </span>
      </div>
      <p className="m-0 mt-3 text-body leading-relaxed text-ink-mid">{fit}</p>
      <p className="m-0 mt-2 break-words font-mono text-label leading-relaxed text-ink-low">
        {models}
      </p>
    </article>
  );
}

function ProviderPanel({
  title,
  rows,
}: {
  readonly title: string;
  readonly rows: readonly {
    readonly label: string;
    readonly value: string;
    readonly mono?: boolean;
  }[];
}): JSX.Element {
  return (
    <article className="rounded-lg border border-line-soft bg-well p-4">
      <h3 className="m-0 text-body font-medium text-ink">{title}</h3>
      <KeyValueList compact rows={rows} />
    </article>
  );
}

function Checklist({
  title,
  items,
}: {
  readonly title: string;
  readonly items: readonly string[];
}): JSX.Element {
  return (
    <div className="rounded-lg border border-line-soft bg-well p-4">
      <h3 className="m-0 text-body font-medium text-ink">{title}</h3>
      <ol className="m-0 mt-3 flex list-none flex-col gap-2 p-0">
        {items.map((item, index) => (
          <li key={item} className="flex gap-2.5">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-raised font-mono text-caption text-forest">
              {index + 1}
            </span>
            <span className="text-body leading-relaxed text-ink-mid">{item}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function ActionLink({
  to,
  label,
  detail,
  testId,
}: {
  readonly to: string;
  readonly label: string;
  readonly detail: string;
  readonly testId?: string;
}): JSX.Element {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="flex flex-col gap-1 rounded-lg border border-line-soft bg-card p-4 transition-colors duration-150 hover:border-line-strong hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
    >
      <span className="text-body font-medium text-ink">{label}</span>
      <span className="text-label text-ink-low">{detail}</span>
    </Link>
  );
}

function PlatformTabs({
  value,
  onChange,
  testIdSuffix = '',
}: {
  readonly value: DocsPlatform;
  readonly onChange: (next: DocsPlatform) => void;
  /** 同页第二个实例（Codex 节）要带后缀避免 testId 撞车；默认空保住既有用例。 */
  readonly testIdSuffix?: string;
}): JSX.Element {
  const t = useT();
  return (
    <Tabs
      value={value}
      onValueChange={(next) => {
        if (next === 'macos' || next === 'windows') onChange(next);
      }}
    >
      <TabsList ariaLabel={t('docs.platform.aria')}>
        <TabsTrigger value="macos" testId={`docs-platform-macos${testIdSuffix}`}>
          {t('docs.platform.macos')}
        </TabsTrigger>
        <TabsTrigger value="windows" testId={`docs-platform-windows${testIdSuffix}`}>
          {t('docs.platform.windows')}
        </TabsTrigger>
      </TabsList>
    </Tabs>
  );
}
